/* global Office */

/**
 * utils/auth.js
 * Authentification Microsoft Entra via Nested App Authentication (NAA).
 *
 * On récupère un jeton d'ACCÈS (access token) pour notre propre API web,
 * exposée dans l'app registration Entra sous la portée `access_as_user`.
 * Ce jeton, signé par Entra, prouve au backend que l'utilisateur appartient bien
 * à notre organisation ET qu'il appelle bien notre API (claim `scp`).
 *
 * Pourquoi un access token et plus l'ID token :
 * MSAL gère lui-même la fraîcheur des access tokens
 * (ssoSilent/acquireTokenSilent les renouvelle silencieusement via le refresh token).
 * L'ID token, lui, n'était jamais réévalué sur sa propre expiration et finissait par être servi périmé.
 *
 * Si l'app Entra n'est pas configurée (REACT_APP_ENTRA_CLIENT_ID vide),
 * on retombe sur le mode dev local :
 * getAccessToken() retourne null et api.js envoie alors l'en-tête X-User-Email.
 */

import {
  createNestablePublicClientApplication,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";

const CLIENT_ID = process.env.REACT_APP_ENTRA_CLIENT_ID || "";
const TENANT_ID = process.env.REACT_APP_ENTRA_TENANT_ID || "common";

// On demande un access token pour notre API exposée dans Entra (« Expose an API ») :
// App ID URI = api://<client-id>, portée = access_as_user.
// MSAL ajoute automatiquement openid/profile/offline_access
// (nécessaires au claim preferred_username et au renouvellement silencieux via refresh token).
const SCOPES = CLIENT_ID ? [`api://${CLIENT_ID}/access_as_user`] : ["User.Read"];

let msalInstance = null;

/**
 * Indice de connexion : l'email de l'utilisateur Outlook déjà connecté.
 * Permet à MSAL/au broker de savoir quel compte cibler dès le premier appel silencieux
 * (sinon, aucun compte n'est encore en cache côté MSAL).
 */
function getLoginHint() {
  return window.Office?.context?.mailbox?.userProfile?.emailAddress || undefined;
}

/** Vrai si l'authentification Entra est configurée (sinon : mode dev). */
export function isAuthConfigured() {
  return Boolean(CLIENT_ID);
}

async function getMsal() {
  if (!msalInstance) {
    msalInstance = await createNestablePublicClientApplication({
      auth: {
        clientId: CLIENT_ID,
        authority: `https://login.microsoftonline.com/${TENANT_ID}`,
      },
      cache: { cacheLocation: "localStorage" },
    });
  }
  return msalInstance;
}

/**
 * Retourne un access token Entra (pour notre API) pour l'utilisateur Outlook connecté.
 * - Tente d'abord le SSO silencieux via ssoSilent + loginHint (aucune interaction).
 * - Si une interaction est requise, ouvre une fenêtre de connexion.
 * - Retourne null si l'auth n'est pas configurée (mode dev local).
 *
 * @param {boolean} forceRefresh
 *   Force MSAL à ignorer le cache et à renouveler le jeton via le refresh token.
 *   Utilisé par api.js pour réessayer après un 401.
 *
 * Modèle officiel NAA : ssoSilent(loginHint) puis acquireTokenPopup en repli
 * uniquement sur InteractionRequiredAuthError.
 */
export async function getAccessToken({ forceRefresh = false } = {}) {
  if (!isAuthConfigured()) return null;

  const msal = await getMsal();
  const request = { scopes: SCOPES, loginHint: getLoginHint(), forceRefresh };

  try {
    const result = await msal.ssoSilent(request);
    return result.accessToken;
  } catch (silentError) {
    if (silentError instanceof InteractionRequiredAuthError) {
      // Consentement / connexion requis : on bascule sur une fenêtre interactive.
      const result = await msal.acquireTokenPopup(request);
      return result.accessToken;
    }
    // Autre erreur (config, réseau…) : on la laisse remonter.
    throw silentError;
  }
}
